import { Lock, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FeatureLockedPageProps {
    /** Name of the feature being displayed */
    featureName: string;
    /** Description of what this feature does */
    description: string;
    /** List of benefits/capabilities of this feature */
    benefits?: string[];
    /** Icon component to display */
    icon?: React.ComponentType<{ className?: string }>;
}

/**
 * Full-page component shown when a user tries to access a locked feature
 * Shows feature information and upgrade prompts
 */
export const FeatureLockedPage = ({
    featureName,
    description,
    benefits = [],
    icon: Icon = Lock,
}: FeatureLockedPageProps) => {
    return (
        <div className="min-h-screen bg-brand-page flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                <div className="bg-card rounded-2xl shadow-xl border border-border overflow-hidden">
                    {/* Header */}
                    <div className="bg-brand-header p-8 text-brand-header-ink">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-20 h-20 bg-card/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <Icon className="w-10 h-10" />
                            </div>
                        </div>
                        <h1 className="text-3xl font-bold text-center mb-2">{featureName}</h1>
                        <p className="text-brand-header-ink/80 text-center text-lg">
                            {description}
                        </p>
                    </div>

                    {/* Content */}
                    <div className="p-8">
                        {/* Upgrade Message */}
                        <div className="bg-card border-l-4 border-l-brand-fill-yellow border border-border rounded-lg p-6 mb-6">
                            <div className="flex items-start">
                                <Sparkles className="w-6 h-6 text-muted-foreground mr-3 shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-semibold text-foreground mb-1">
                                        Upgrade to Access This Feature
                                    </h3>
                                    <p className="text-muted-foreground text-sm">
                                        This feature is not included in your current plan. Upgrade
                                        to unlock the full potential of Light2Live and achieve your
                                        wellness goals.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Benefits List */}
                        {benefits.length > 0 && (
                            <div className="mb-8">
                                <h3 className="font-semibold text-foreground mb-4">
                                    What you'll get with {featureName}:
                                </h3>
                                <ul className="space-y-3">
                                    {benefits.map((benefit, index) => (
                                        <li key={index} className="flex items-start">
                                            <Check className="w-5 h-5 text-brand-text-green mr-3 shrink-0 mt-0.5" />
                                            <span className="text-foreground">{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Button className="flex-1" size="lg">
                                <Sparkles className="w-4 h-4 mr-2" />
                                Upgrade Now
                            </Button>
                            <Button
                                variant="outline"
                                className="flex-1"
                                size="lg"
                                onClick={() => window.history.back()}
                            >
                                Go Back
                            </Button>
                        </div>

                        {/* Help Text */}
                        <p className="text-center text-sm text-muted-foreground mt-6">
                            Have questions? Contact your wellness coordinator or email{' '}
                            <a
                                href="mailto:support@light2live.com"
                                className="text-brand-text-blue hover:underline"
                            >
                                support@light2live.com
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Pre-configured pages for common features
export const CoursesLockedPage = () => (
    <FeatureLockedPage
        featureName="Wellness Courses"
        description="Access expert-led courses on nutrition, mindfulness, and healthy living"
        benefits={[
            'Access to 50+ expert-led wellness courses',
            'Track your progress and earn certificates',
            'Weekly meal plans designed by nutritionists',
            'Interactive quizzes and learning materials',
            'Lifetime access to course content',
        ]}
    />
);

export const MealPlanningLockedPage = () => (
    <FeatureLockedPage
        featureName="Meal Planning"
        description="Plan your meals and achieve your nutrition goals"
        benefits={[
            'Create personalized weekly meal plans',
            'Access to thousands of healthy recipes',
            'Automatic shopping list generation',
            'Nutritional tracking and insights',
            'Meal prep guides and tips',
        ]}
    />
);

export const ShoppingListsLockedPage = () => (
    <FeatureLockedPage
        featureName="Shopping Lists"
        description="Smart shopping lists integrated with your meal plans"
        benefits={[
            'Automatically generated from meal plans',
            'Organize by store sections',
            'Track prices and budget',
            'Share lists with family members',
            'Check off items as you shop',
        ]}
    />
);

export const PantryLockedPage = () => (
    <FeatureLockedPage
        featureName="Pantry Tracking"
        description="Keep track of your pantry inventory and reduce food waste"
        benefits={[
            'Track what you have on hand',
            'Get expiration date reminders',
            'Reduce food waste and save money',
            'Recipe suggestions based on pantry items',
            'Inventory reports and analytics',
        ]}
    />
);

export const JournalLockedPage = () => (
    <FeatureLockedPage
        featureName="Wellness Journal"
        description="Track your daily wellness journey and build healthy habits"
        benefits={[
            'Daily mood and energy tracking',
            'Gratitude and reflection prompts',
            'Habit tracking and goal setting',
            'Visual progress charts',
            'Export your journal entries',
        ]}
    />
);

export const MealLoggingLockedPage = () => (
    <FeatureLockedPage
        featureName="Meal Logging"
        description="Log your meals and track your nutritional intake"
        benefits={[
            'Quick and easy meal logging',
            'Nutritional breakdown and insights',
            'Photo documentation of meals',
            'Track portions and calories',
            'Integration with wellness score',
        ]}
    />
);
